import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import authService from '../../api/services/authService';
import LoginForm from '../../components/auth/LoginForm/LoginForm';
import ResetPassword from '../../components/auth/ResetPassword/ResetPassword';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import OTPVerification from '../../components/auth/OTPVerification/OTPVerification';
import ForgotPassword from '../../components/auth/ForgotPassword/ForgotPassword';
import FeatureShowcase from '../../components/auth/FeatureShowcase/FeatureShowcase';
import BottomFeatures from '../../components/auth/BottomFeatures/BottomFeatures';
import { motion } from 'framer-motion';

const Login = () => {
  const { login, loginWithOTP, verifyOTP: verifyOTPContext } = useAuth();
  const [currentView, setCurrentView] = useState('login');
  const [userCredentials, setUserCredentials] = useState(null);

  // Mutations
  const loginMutation = useMutation({
    mutationFn: ({ email, password, rememberMe }) => login(email, password, rememberMe),
    onSuccess: (result) => {
      if (result.success) {
        toast.dismiss('auth-toast');
        toast.success('Login successful!');
      } else {
        toast.error(result.error || 'Login failed', { id: 'auth-toast' });
      }
    },
    onError: (error) => {
      toast.error(error.message || 'An unexpected error occurred', { id: 'auth-toast' });
    }
  });

  const otpLoginMutation = useMutation({
    mutationFn: ({ emailOrPhone, inputType }) => {
      if (inputType === 'phone') {
        let phoneNumber = emailOrPhone;
        if (!phoneNumber.startsWith('+')) phoneNumber = '+91' + phoneNumber;
        return authService.loginMobileOTP(phoneNumber);
      }
      return authService.loginWithEmailOTP(emailOrPhone);
    },
    onSuccess: (result, variables) => {
      if (result.success) {
        toast.dismiss('auth-toast');
        toast.success('OTP sent successfully!');
        setUserCredentials({
          emailOrPhone: variables.emailOrPhone,
          inputType: variables.inputType,
          rememberMe: variables.rememberMe,
          otpData: result.data
        });
        setCurrentView('otp');
      } else {
        toast.error(result.error || 'Failed to send OTP', { id: 'auth-toast' });
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send OTP. Please try again.', { id: 'auth-toast' });
    }
  });

  const verifyOTPLoginMutation = useMutation({
    mutationFn: async ({ otp }) => {
      let phoneNumber = userCredentials.emailOrPhone;
      if (userCredentials.inputType === 'phone' && !phoneNumber.startsWith('+')) {
        phoneNumber = '+91' + phoneNumber;
      }

      const verifyFn = userCredentials.inputType === 'phone'
        ? authService.verifyMobileOTP(phoneNumber, otp)
        : authService.verifyEmailOTP(userCredentials.emailOrPhone, otp);

      return verifyFn;
    },
    onSuccess: async (result) => {
      if (result.success) {
        toast.success('OTP verified successfully!');
        setUserCredentials(null);
        await loginWithOTP(result.user);
      } else {
        toast.error(result.error || 'OTP verification failed');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'OTP verification failed');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ email, otp, newPassword }) => authService.resetPassword(email, otp, newPassword),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Password reset successfully!');
        setCurrentView('login');
      } else {
        toast.error(result.error || 'Failed to reset password');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reset password. Please try again.');
    }
  });

  const handleLogin = (email, password, rememberMe) => {
    loginMutation.mutate({ email, password, rememberMe });
  };

  const handleForgotPassword = async (email) => {
    setCurrentView('forgot-password');
  };

  const handleResetPassword = () => {
    setCurrentView('reset-password');
  };

  const handleResetPasswordSubmit = (email, otp, newPassword) => {
    resetPasswordMutation.mutate({ email, otp, newPassword });
  };

  const handleOTPLogin = (emailOrPhone, inputType, rememberMe) => {
    otpLoginMutation.mutate({ emailOrPhone, inputType, rememberMe });
  };

  const handleVerifyOTP = (otp) => {
    if (!userCredentials || !userCredentials.emailOrPhone) {
      toast.error('Session expired. Please login again.');
      setCurrentView('login');
      return;
    }

    verifyOTPLoginMutation.mutate({ otp });
  };

  const handleBackToLogin = () => {
    setCurrentView('login');
    setUserCredentials(null);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'otp':
        return (
          <OTPVerification
            onBackToLogin={handleBackToLogin}
            onVerifyOTP={handleVerifyOTP}
            loading={verifyOTPLoginMutation.isPending}
            userEmail={userCredentials?.emailOrPhone}
            otpData={userCredentials?.otpData}
            userCredentials={userCredentials}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPassword
            onBackToLogin={handleBackToLogin}
          />
        );
      case 'reset-password':
        return (
          <ResetPassword
            onBackToLogin={handleBackToLogin}
            onResetPassword={handleResetPasswordSubmit}
            loading={resetPasswordMutation.isPending}
          />
        );
      default:
        return (
          <LoginForm
            onLogin={handleLogin}
            onForgotPassword={handleForgotPassword}
            onOTPLogin={handleOTPLogin}
            onResetPassword={handleResetPassword}
            loading={loginMutation.isPending || otpLoginMutation.isPending}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-hrms-bg font-sans flex flex-col justify-between">
      {/* Main content area — centered card */}
      <div className="flex-1 flex items-center justify-center p-3 sm:p-6 lg:p-8 my-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-[1200px] bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-login-card ring-1 ring-slate-900/5 overflow-hidden transition-all duration-300"
        >
          <div className="flex flex-col lg:flex-row h-full lg:min-h-[580px]">
            {/* Left Panel - Login Form */}
            <div className="w-full lg:w-[45%] flex flex-col justify-center p-5 sm:p-8 lg:p-10 xl:p-12 overflow-y-auto lg:min-h-[580px]">
              <motion.div
                key={currentView}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="w-full my-auto"
              >
                {renderCurrentView()}
              </motion.div>
            </div>

            {/* Right Panel - Feature Showcase (hidden on mobile/tablet) */}
            <div className="hidden lg:block lg:w-[55%] min-h-[580px]">
              <FeatureShowcase />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Feature Strip — full width */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <BottomFeatures />
      </motion.div>
    </div>
  );
};

export default Login;
